import { useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users, ShieldCheck, GraduationCap, Wrench, Truck, BarChart3, Lock, ChevronRight, X,
} from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { getAccessibleSubPlatforms } from '@config/platforms';
import { openPlatformTarget } from '@utils/platformNav';

const ICONS = {
  'users':          Users,
  'shield-check':   ShieldCheck,
  'graduation-cap': GraduationCap,
  'wrench':         Wrench,
  'truck':          Truck,
  'bar-chart-3':    BarChart3,
};

const subVariants = {
  hidden:  { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.15 + i * 0.08, duration: 0.45, ease: [0.23, 1, 0.32, 1] },
  }),
};

function SubCard({ sub, platform, accessible, index }) {
  const navigate = useNavigate();
  const Icon = ICONS[sub.icon] || BarChart3;
  const isLocked = !accessible;

  return (
    <motion.button
      type="button"
      data-testid="sub-platform-card"
      custom={index}
      variants={subVariants}
      initial="hidden"
      animate="visible"
      whileHover={isLocked ? undefined : { scale: 1.02 }}
      whileTap={isLocked ? undefined : { scale: 0.98 }}
      onClick={() => {
        if (isLocked) return;
        openPlatformTarget(
          { platformId: platform.id, externalApp: sub.externalApp, path: sub.path },
          navigate
        );
      }}
      className={`
        relative w-full text-left rounded-xl border p-5 flex flex-col gap-3
        transition-colors duration-300
        ${isLocked
          ? 'border-white/5 opacity-50 cursor-not-allowed'
          : 'border-white/8 hover:border-[var(--color)]/40 cursor-pointer'
        }
      `}
      style={{
        '--color':  platform.color,
        background: 'rgba(8, 19, 32, 0.7)',
      }}
    >
      <div className="flex items-start justify-between">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{
            background: `${platform.color}15`,
            border:     `1px solid ${platform.color}30`,
          }}
        >
          <Icon size={18} style={{ color: platform.color }} strokeWidth={1.5} />
        </div>
        <span className="text-2xs font-mono uppercase tracking-widest text-gl-muted">
          {isLocked ? 'RESTRICTED' : sub.type === 'dashboard' ? 'ANALYTICS' : 'PLATFORM'}
        </span>
      </div>

      <div>
        <h4 className="text-base font-display font-semibold text-gl-ghost leading-tight">
          {sub.title}
        </h4>
        <p className="text-xs text-gl-muted leading-relaxed mt-1">{sub.description}</p>
      </div>

      <div className="flex items-center justify-between mt-auto pt-2 border-t border-white/5">
        {isLocked
          ? <Lock size={12} className="text-gl-muted" />
          : <span className="text-2xs font-mono text-gl-muted uppercase tracking-wider">Open</span>}
        <ChevronRight size={14} style={{ color: isLocked ? '#8199B4' : platform.color }} />
      </div>
    </motion.button>
  );
}

/**
 * SectionPanel — expanded view of a platform card (shared-layout morph).
 *
 * Renders a backdrop + centered panel (full-width bottom sheet on mobile)
 * carrying the same layoutId as the originating PlatformCard, so Framer
 * morphs the card into the panel. Shows the section's sub-cards: dashboard
 * area + the platform itself, each individually RBAC-gated.
 */
export function SectionPanel({ platform, onClose }) {
  const { user } = useAuth();
  const reducedMotion = useReducedMotion();
  const accentEnd = platform.colorEnd || platform.color;
  const accessibleSubs = getAccessibleSubPlatforms(platform, user);
  const Icon = ICONS[platform.icon] || BarChart3;

  // Esc closes; page scroll locked while open
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-center justify-center sm:p-6"
      data-testid="section-panel"
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
        style={{ background: 'rgba(5, 8, 17, 0.7)', backdropFilter: 'blur(8px)' }}
      />

      {/* Panel — morphs from the clicked card */}
      <motion.div
        layoutId={reducedMotion ? undefined : `platform-${platform.id}`}
        initial={reducedMotion ? { opacity: 0 } : false}
        animate={reducedMotion ? { opacity: 1 } : undefined}
        exit={{ opacity: 0, transition: { duration: 0.2 } }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="relative w-full sm:max-w-2xl max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/10"
        style={{
          background: 'rgba(10, 16, 35, 0.92)',
          backdropFilter: 'blur(24px) saturate(180%)',
          boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px ${platform.color}25, 0 0 60px ${platform.color}12`,
        }}
      >
        {/* Top accent bar */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${platform.color}, ${accentEnd}, transparent)` }}
        />

        <div className="p-6 sm:p-8 flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: `${platform.color}15`,
                  border:     `1px solid ${platform.color}30`,
                }}
              >
                <Icon size={22} style={{ color: platform.color }} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-display font-semibold text-gl-ghost leading-tight">
                  {platform.title}
                </h3>
                <p className="text-xs text-gl-muted font-mono mt-0.5 tracking-wide uppercase">
                  {platform.subtitle}
                </p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close"
              data-testid="section-panel-close"
              onClick={onClose}
              className="p-2 rounded-lg border border-white/8 text-gl-muted hover:text-gl-ghost hover:border-white/20 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          <p className="text-sm text-gl-muted leading-relaxed">{platform.description}</p>

          {/* Sub-cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(platform.sub || []).map((sub, i) => (
              <SubCard
                key={sub.id}
                sub={sub}
                platform={platform}
                index={i}
                accessible={accessibleSubs.some((s) => s.id === sub.id)}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
